import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type Theme = 'light' | 'dark';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  #window = inject(Window);

  static readonly DEFAULT_THEME: Theme = 'light';

  static prefersColorSchemeSelector = '(prefers-color-scheme: dark)';

  #colorSchemeChanges = this.#window.matchMedia(
    ThemeService.prefersColorSchemeSelector,
  );

  #userPrefersDarkMode = (): boolean => {
    return this.#colorSchemeChanges.matches;
  };

  private readonly themeSubject = new BehaviorSubject<Theme>(
    ThemeService.DEFAULT_THEME,
  );

  public readonly theme$ = this.themeSubject.asObservable();

  private onColorSchemeChange = ({ matches }: MediaQueryListEvent): void => {
    if (matches) {
      return this.set('dark');
    }

    return this.set(ThemeService.DEFAULT_THEME);
  };

  constructor() {
    this.subscribeToColorSchemeChanges();
    this.setInitialTheme();
  }

  public getTheme(): Theme {
    return this.themeSubject.getValue();
  }

  private subscribeToColorSchemeChanges(): void {
    this.#colorSchemeChanges.addEventListener(
      'change',
      this.onColorSchemeChange,
    );
  }

  private setInitialTheme(): void {
    if (this.#userPrefersDarkMode()) {
      return this.set('dark');
    }

    return this.set(ThemeService.DEFAULT_THEME);
  }

  public set(theme: Theme): void {
    this.themeSubject.next(theme);

    document.body.setAttribute('data-tm-theme', theme);
  }
}
